import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { AccessControlScopeConfigService, ScopeFieldOption } from 'src/app/global/services/access-control/access-control-scope-config.service';

@Component({
    selector: 'app-access-control-banner',
    templateUrl: './access-control-banner.component.html',
    styleUrls: ['./access-control-banner.component.scss'],
})
export class AccessControlBannerComponent implements OnInit {
    @Input() formGroup!: FormGroup;

    @Input() formName = '';
    @Input() label = '';

    public permissionsForm: FormGroup;
    public isExpanded = false;

    // Store observables for async pipe
    public resourceOptions$: Observable<ScopeFieldOption[]> = of([]);
    public resourceIdOptionsMap = new Map<number, Observable<ScopeFieldOption[]>>();

    constructor(
        private fb: FormBuilder,
        private scopeConfigService: AccessControlScopeConfigService,
    ) {}

    ngOnInit(): void {
        this.permissionsForm = this.formGroup.get(this.formName) as FormGroup;

        // Load resource options if module uses dropdowns
        if (this.usesDropdowns) {
            this.resourceOptions$ = this.scopeConfigService.getResourceOptions(this.formName);

            // Subscribe to existing scopes to load their resourceId options
            this.scopes.controls.forEach((scope, index) => {
                const resourceValue = scope.get('resource')?.value;
                if (resourceValue) {
                    this.loadResourceIdOptions(index, resourceValue);
                }
            });
        }
    }

    get isActive(): boolean {
        return this.permissionsForm?.get('active')?.value || false;
    }

    get scopes(): FormArray {
        return this.permissionsForm?.get('scopes') as FormArray;
    }

    toggleExpansion(): void {
        if (this.isActive) {
            this.isExpanded = !this.isExpanded;
        }
    }

    addScope(): void {
        const scopeGroup = this.fb.group({
            permission: [this.formName, Validators.required],
            resource: ['', Validators.required],
            resourceId: ['', Validators.required],
            accessLevel: ['read', Validators.required],
        });
        const newIndex = this.scopes.length;
        this.scopes.push(scopeGroup);

        // Initialize empty resourceId options for new scope
        this.resourceIdOptionsMap.set(newIndex, of([]));
    }

    removeScope(index: number): void {
        this.scopes.removeAt(index);

        // Clean up resourceId options map
        this.resourceIdOptionsMap.delete(index);

        // Rebuild the map with correct indices
        const newMap = new Map<number, Observable<ScopeFieldOption[]>>();
        this.scopes.controls.forEach((scope, i) => {
            const existingOptions = this.resourceIdOptionsMap.get(i > index ? i + 1 : i);
            if (existingOptions) {
                newMap.set(i, existingOptions);
            } else {
                newMap.set(i, of([]));
            }
        });
        this.resourceIdOptionsMap = newMap;
    }

    getScopeCount(): number {
        return this.scopes?.length || 0;
    }

    get usesDropdowns(): boolean {
        return this.scopeConfigService.usesDropdowns(this.formName);
    }

    getResourceIdOptions(index: number): Observable<ScopeFieldOption[]> {
        return this.resourceIdOptionsMap.get(index) || of([]);
    }

    onResourceChange(scopeGroup: FormGroup, index: number): void {
        if (this.usesDropdowns) {
            const resourceValue = scopeGroup.get('resource')?.value;

            // Clear resourceId when resource type changes
            scopeGroup.get('resourceId')?.setValue('');

            // Load new resourceId options
            if (resourceValue) {
                this.loadResourceIdOptions(index, resourceValue);
            } else {
                this.resourceIdOptionsMap.set(index, of([]));
            }
        }
    }

    private loadResourceIdOptions(index: number, resourceType: string): void {
        const options$ = this.scopeConfigService.getResourceIdOptions(this.formName, resourceType);
        this.resourceIdOptionsMap.set(index, options$);

        // Auto-select if there's only one option
        options$.subscribe((options) => {
            if (options.length === 1) {
                const scopeControl = this.scopes.at(index);
                scopeControl?.get('resourceId')?.setValue(options[0].value);
            }
        });
    }
}
